import { create } from 'zustand';

interface AdAccount {
  id: string;
  name: string;
  platformAdAccountId: string;
  currency: string;
  status: string;
}

interface AdStoreState {
  selectedAdAccount: AdAccount | null;
  adAccounts: AdAccount[];
  isLoading: boolean;
  error: string | null;
}

interface AdStoreActions {
  setSelectedAdAccount: (account: AdAccount | null) => void;
  setAdAccounts: (accounts: AdAccount[]) => void;
  addAdAccount: (account: AdAccount) => void;
  removeAdAccount: (adAccountId: string) => void;
  updateAdAccount: (adAccountId: string, updates: Partial<AdAccount>) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState: AdStoreState = {
  selectedAdAccount: null,
  adAccounts: [],
  isLoading: false,
  error: null,
};

export const useAdStore = create<AdStoreState & AdStoreActions>((set) => ({
  ...initialState,

  setSelectedAdAccount: (account) => set({ selectedAdAccount: account }),
  setAdAccounts: (accounts) => set({ adAccounts: accounts }),
  addAdAccount: (account) =>
    set((state) => ({ adAccounts: [...state.adAccounts, account] })),
  removeAdAccount: (adAccountId) =>
    set((state) => ({
      adAccounts: state.adAccounts.filter((a) => a.id !== adAccountId),
      selectedAdAccount:
        state.selectedAdAccount?.id === adAccountId
          ? null
          : state.selectedAdAccount,
    })),
  updateAdAccount: (adAccountId, updates) =>
    set((state) => ({
      adAccounts: state.adAccounts.map((a) =>
        a.id === adAccountId ? { ...a, ...updates } : a
      ),
      selectedAdAccount:
        state.selectedAdAccount?.id === adAccountId
          ? { ...state.selectedAdAccount, ...updates }
          : state.selectedAdAccount,
    })),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  reset: () => set(initialState),
}));